"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect, useState } from "react";
import { isHappeningNow } from "~/lib/events/driver-events";

/**
 * Whether an event day is under way right now, kept true over time.
 *
 * Reading `isHappeningNow(...)` straight in render answers the question once, at
 * first paint, and then never again: a card that goes live while the list is
 * open never grows the pill, and one whose day ends keeps claiming "Happening
 * now" until something else re-renders it. `/events` is a browse surface people
 * leave open, so both are reachable.
 *
 * Starts `false` and turns on from an effect, so the server-rendered markup and
 * the first client render agree — the clock is never read during render.
 *
 * The timer is armed for the next transition (start, then end) rather than
 * polling, and clamped because `setTimeout` overflows past ~24.8 days and would
 * otherwise fire immediately, spinning on a far-future event.
 */

/** Longest single sleep. Well under the `setTimeout` overflow, and re-arms on wake. */
const MAX_TIMER_MS = 6 * 60 * 60_000;
export function useIsHappeningNow(startsAt, endsAt) {
  const $ = _c(4);
  const [live, setLive] = useState(false);
  let t0;
  let t1;
  if ($[0] !== endsAt || $[1] !== startsAt) {
    t0 = () => {
      const start = Date.parse(startsAt);
      const end = Date.parse(endsAt);
      if (Number.isNaN(start) || Number.isNaN(end)) {
        setLive(false);
        return;
      }
      let timer;
      const tick = () => {
        const now = Date.now();
        setLive(isHappeningNow(startsAt, endsAt, now));
        const next = now < start ? start : now < end ? end : null;
        if (next === null) {
          return;
        }
        timer = setTimeout(tick, Math.min(next - now + 1000, MAX_TIMER_MS));
      };
      tick();
      return () => clearTimeout(timer);
    };
    t1 = [startsAt, endsAt];
    $[0] = endsAt;
    $[1] = startsAt;
    $[2] = t0;
    $[3] = t1;
  } else {
    t0 = $[2];
    t1 = $[3];
  }
  useEffect(t0, t1);
  return live;
}